import { useRef, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { useUserAgents } from "@/hooks/agent/useAgents";
import type { InboxViewSuggestion, InboxViewTimeRange } from "@/types/inboxView/inboxView";
import { GenieScopePicker } from "./GenieScopePicker";
import { TimeRangePicker } from "./TimeRangePicker";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface Props {
  agentFilter: string[] | null;
  onAgentFilterChange: (next: string[] | null) => void;
  timeRange: InboxViewTimeRange;
  onTimeRangeChange: (next: InboxViewTimeRange) => void;
  onSubmit: (prompt: string) => void;
  disabled?: boolean;
  suggestions: InboxViewSuggestion[] | null;
  suggestionsLoading: boolean;
  suggestionReason?: string | null;
  onFetchSuggestions: () => void;
  onPickSuggestion: (suggestion: InboxViewSuggestion) => void;
  pickingTitle: string | null;
  showSuggestionChips: boolean;
}

const scopeLabel = (
  agentFilter: string[] | null,
  agents: Array<{ id: string; name: string }>,
): string => {
  if (!agentFilter || agentFilter.length === 0) return "your Genies";
  if (agentFilter.length === 1) {
    const match = agents.find((a) => a.id === agentFilter[0]);
    return match?.name ?? "your Genie";
  }
  return `your ${agentFilter.length} Genies`;
};

export const HeroPrompt = ({
  agentFilter,
  onAgentFilterChange,
  timeRange,
  onTimeRangeChange,
  onSubmit,
  disabled,
  suggestions,
  suggestionsLoading,
  suggestionReason,
  onFetchSuggestions,
  onPickSuggestion,
  pickingTitle,
  showSuggestionChips,
}: Props) => {
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { data: agents = [] } = useUserAgents(100);

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    setText("");
  };

  return (
    <div>
      {/* Filters sit above the prompt so the customer sees which Genies and
          what time window the view will be scoped to BEFORE they type. */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <GenieScopePicker
          selected={agentFilter}
          onChange={onAgentFilterChange}
          disabled={disabled}
        />
        <TimeRangePicker value={timeRange} onChange={onTimeRangeChange} disabled={disabled} />
      </div>

      {/* Make the all-genies default obvious. Users have lost work by typing a
          view, hitting Create, and only then realising they pulled in every
          Genie. Surface the implication BEFORE they type. */}
      {(!agentFilter || agentFilter.length === 0) && (
        <div className="mb-3 text-xs text-[#64748B] flex items-start gap-1.5">
          <GenieIcon name="sparkles" size={14} color="#4E9CFF" label="Tip" className="mt-0.5 shrink-0" />
          <span>
            This view will analyze conversations from <span className="font-semibold text-[#222B45]">every Genie you have</span>.{" "}
            Pick a specific Genie above to focus the view on just that one.
          </span>
        </div>
      )}

      <div
        className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]"
        style={{ boxShadow: "0 20px 60px -20px rgba(78,156,255,.35), 0 8px 30px -10px rgba(255,77,184,.25)" }}
      >
        <div className="bg-white rounded-[14px] px-5 py-4 flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] flex items-center justify-center shrink-0">
            <GenieIcon
              name="sparkles"
              size={20}
              color="#ffffff"
              label="Help Genie"
            />
          </div>
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              const el = inputRef.current;
              if (el) {
                el.style.height = "auto";
                el.style.height = Math.min(el.scrollHeight, 200) + "px";
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            rows={1}
            placeholder="Describe a view in plain English... or pick one of the ideas below"
            disabled={disabled}
            className="flex-1 border-0 outline-none resize-none text-base leading-snug text-[#222B45] placeholder:text-[#B6C0CC] disabled:opacity-60 disabled:cursor-not-allowed"
          />
          <button
            onClick={submit}
            disabled={disabled || !text.trim()}
            className="shrink-0 px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
          >
            Create view
          </button>
        </div>
      </div>

      {showSuggestionChips && (
        <SuggestionChips
          scope={scopeLabel(agentFilter, agents)}
          suggestions={suggestions}
          loading={suggestionsLoading}
          reason={suggestionReason ?? null}
          onPick={onPickSuggestion}
          pickingTitle={pickingTitle}
          disabled={disabled}
          onFetch={onFetchSuggestions}
        />
      )}
    </div>
  );
};

interface ChipsProps {
  scope: string;
  suggestions: InboxViewSuggestion[] | null;
  loading: boolean;
  reason: string | null;
  onPick: (s: InboxViewSuggestion) => void;
  pickingTitle: string | null;
  disabled?: boolean;
  onFetch: () => void;
}

// One-click suggestion chips, generated by sampling the customer's actual
// transcripts (scoped to whichever Genies are selected). Opt-in: the customer
// has to click "Get ideas" so we never show stale suggestions for the wrong
// filter scope.
const SuggestionChips = ({ scope, suggestions, loading, reason, onPick, pickingTitle, disabled, onFetch }: ChipsProps) => {
  if (loading) {
    return (
      <div className="mt-3">
        <div className="inline-flex items-center gap-1.5 text-xs text-[#64748B] mb-2">
          <GenieIcon name="sparkles" size={14} color="#4E9CFF" label="Genie ideas" />
          <span>Reading {scope}'s conversations for ideas...</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-7 w-48 rounded-full bg-[#FAFCFF] border border-[#E5EAF0] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // No fetch yet — surface a CTA so the customer triggers it explicitly.
  if (suggestions === null) {
    return (
      <div className="mt-3">
        <button
          type="button"
          onClick={onFetch}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-genie-primary/30 bg-white text-genie-primary hover:bg-genie-primary/5 transition-colors disabled:opacity-50"
        >
          <GenieIcon name="sparkles" size={12} color="#4E9CFF" label="" />
          Get tailored ideas for {scope}
        </button>
      </div>
    );
  }

  // Empty result. Quiet for "no_conversations" (the user knows), small retry
  // for everything else.
  if (suggestions.length === 0) {
    if (reason === "no_conversations") return null;
    return (
      <div className="mt-3">
        <button
          type="button"
          onClick={onFetch}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-genie-primary hover:opacity-80 disabled:opacity-40"
        >
          <RotateCcw className="h-3 w-3" />
          Couldn't suggest ideas — try again
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-1.5 text-xs text-[#64748B]">
          <GenieIcon name="sparkles" size={14} color="#4E9CFF" label="Genie ideas" />
          <span>One-click ideas for <span className="font-semibold text-[#222B45]">{scope}</span> — click to create:</span>
        </div>
        <button
          type="button"
          onClick={onFetch}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-genie-primary hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <RotateCcw className="h-3 w-3" />
          New ideas
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((s) => {
          const pending = pickingTitle === s.title;
          return (
            <button
              key={s.title}
              onClick={() => onPick(s)}
              disabled={disabled || pending}
              title={s.prompt}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#E5EAF0] bg-white text-[#222B45] hover:border-genie-primary/40 hover:text-genie-primary hover:bg-[#FAFCFF] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {pending ? (
                <Loader2 className="h-3 w-3 animate-spin text-genie-primary" />
              ) : (
                <GenieIcon name="sparkles" size={12} color="#4E9CFF" label="Idea" />
              )}
              <span className="font-medium">{s.title}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
